import React, { useMemo } from "react";
import { connect } from "react-redux";
import { store } from "../../../store/configureStore";
import { lS, scroll } from "../../../atlas-utils";
import { ActionTypes } from "../../../actions/_types";
import ReactTable from "../../../components/Periscope/NewTable/ReactTable";
import EntityHead from "../../../components/Periscope/NewTable/EntityHead";
import EntityCell from "../../../components/Periscope/NewTable/EntityCell";
import { getPlatformDetailViaName } from "../../../helpers/periscope";
import { CATALOGUE_PLATFORMS_LOGO } from "../../../client-config";
import EntityCard from "../../../components/Periscope/NewTable/EntityCard";

const SelectedItemTable = ({
	periscopeSpecificItemAvailabilityItemData,
	defaultPlatforms,
	periscopeSpecificItemAvailabilityItemState,
	tableRef,
	configItems,
	login,
	periscopeCountryWiseSupportedPlatforms,
	refreshBtn
}) => {
	const data = periscopeSpecificItemAvailabilityItemData?.data?.objects?.data;
	const dataCount = periscopeSpecificItemAvailabilityItemData?.data?.objects?.meta?.totalCount;
	const isLoading = periscopeSpecificItemAvailabilityItemData?.loading;
	const error = periscopeSpecificItemAvailabilityItemData?.error;
	const appliedPlatformNames = periscopeSpecificItemAvailabilityItemState?.appliedFilters?.platform_names;
	const offset = periscopeSpecificItemAvailabilityItemState?.offset;
	const limit = periscopeSpecificItemAvailabilityItemState?.limit;
	const handlePagination = async (page) => {
		const offset = (page - 1) * limit;
		store.dispatch({
			type: ActionTypes.SPECIFIC_ITEM_AVAILABILITY_STATE_ITEM_FILTERS,
			payload: { offset: offset }
		});

		if (tableRef.current) {
			scroll({ top: tableRef.current.offsetTop - 57, left: 0 });
		}
	};
	const country = (login?.loggedInbizDetail?.country || lS.get("auth")?.biz?.country).toLowerCase();
	const bizPlatforms = useMemo(() => {
		return configItems?.bizPlatforms?.items.filter((platform) => {
			if (platform.platformName == "All Platforms") {
				return true;
			}
			return (periscopeCountryWiseSupportedPlatforms?.[country] ||
				lS.get("periscopeCountryWisePlatforms")?.[country])?.[platform.platformName.toLowerCase()]?.enabled;
		});
	}, [periscopeCountryWiseSupportedPlatforms?.[country]]);

	const handlePageSize = async (field, size) => {
		store.dispatch({
			type: ActionTypes.SPECIFIC_ITEM_AVAILABILITY_STATE_ITEM_FILTERS,
			payload: { limit: size.value }
		});
		store.dispatch({
			type: ActionTypes.SPECIFIC_ITEM_AVAILABILITY_STATE_ITEM_FILTERS,
			payload: { offset: 0 }
		});
		if (tableRef.current) {
			scroll({ top: tableRef.current.offsetTop - 57, left: 0 });
		}
	};

	const currentPlatforms =
		appliedPlatformNames?.length > 0 && appliedPlatformNames?.[0] !== "all"
			? appliedPlatformNames
			: defaultPlatforms;
	const totalPercent = 100 / currentPlatforms.length;
	const platformCount = useMemo(() => String(`${totalPercent}%`), [totalPercent]);
	const tableData = useMemo(() => {
		return ["location", ...currentPlatforms];
	}, [currentPlatforms]);

	const dispatchSortFunction = (sort = null) => {
		if (sort == null) {
			store.dispatch({
				type: ActionTypes.SPECIFIC_ITEM_AVAILABILITY_ITEM_INITIAL_STATE_SORT
			});
		} else {
			store.dispatch({
				type: ActionTypes.SPECIFIC_ITEM_AVAILABILITY_STATE_ITEM_FILTERS,
				payload: sort
			});
		}
	};

	let displaySortImages = tableData.length > 2;
	const handleClickSort = (column) => {
		if (displaySortImages && column !== "location") {
			store.dispatch({
				type: ActionTypes.SPECIFIC_ITEM_AVAILABILITY_STATE_ITEM_FILTERS,
				payload: { offset: 0 }
			});
			const { sort } = periscopeSpecificItemAvailabilityItemState || {};
			let order = "";
			if (sort?.field === column) {
				order = sort?.order === "asc" ? "desc" : sort?.order === "desc" ? null : "asc";
			} else {
				order = "asc";
			}
			const newSort = order ? { sort: { field: column, order } } : null;
			dispatchSortFunction(newSort);
		}
	};

	const duplicateValues = useMemo(() => {
		let valuesPattern = ["null", "null", "null", "undefined", "undefined", "undefined"];
		let indexInPattern = 0;
		return Array.from({ length: 9 }).map((_) => {
			let obj = {};
			[...tableData].forEach((key) => {
				obj[key] = valuesPattern[indexInPattern];
			});
			indexInPattern = (indexInPattern + 1) % valuesPattern.length;
			return obj;
		});
	}, [tableData, defaultPlatforms, currentPlatforms]);

	const columnsData = useMemo(() => {
		return tableData.map((col) => {
			return {
				id: col,
				render: () => {
					return (
						<EntityHead
							title={col}
							{...(col !== "location" && {
								imgSrc:
									getPlatformDetailViaName(col, bizPlatforms)?.image || CATALOGUE_PLATFORMS_LOGO[col],
								sort: currentPlatforms && currentPlatforms.length > 1,
								handleSortClick: handleClickSort,
								sortOrder: periscopeSpecificItemAvailabilityItemState.sort?.order,
								sortedField: periscopeSpecificItemAvailabilityItemState.sort?.field
							})}
						/>
					);
				}
			};
		});
	}, [tableData, bizPlatforms, periscopeSpecificItemAvailabilityItemState.sort]);

	const renderCellToolTip = (data, rest) => {
		const propsValue = rest?.propsValue;
		if (!propsValue) {
			return <></>;
		}
		const total = propsValue?.total;
		const available = propsValue?.available;
		if (total === 0) {
			return <span> The storefront does not exist </span>;
		} else if (total == available) {
			return <span>This item is online</span>;
		} else if (total > available) {
			return (
				<span>
					This item is
					<span className="span-offline"> Offline </span>
				</span>
			);
		} else {
			return <div></div>;
		}
	};

	const newColumns = useMemo(() => {
		return Object.keys(
			isLoading || error ? duplicateValues?.[0] || {} : (data && data.length > 0 && data?.[0]) || {}
		).map((obj) => ({
			accessor: obj,
			enableRowSpan: false,

			Cell: (props) => {
				if (props.column.id == "location") {
					return (
						<EntityCard
							loading={isLoading}
							error={error}
							rating={props?.value?.rating}
							ratingText
							star
							title={props.value?.location_name}
						/>
					);
				} else {
					const total = props?.value?.total;
					const available = props?.value?.available;
					const stringValue =
						total == available && total !== 0 && available !== 0
							? "true-state"
							: total == 0
								? "empty-state"
								: "false-state";

					return (
						<EntityCell
							loading={isLoading}
							error={error}
							classes={stringValue}
							value={stringValue == "true-state" ? true : stringValue == "false-state" ? false : null}
							customToolTip
							renderToolTip={renderCellToolTip}
							propsValue={props?.value}
						/>
					);
				}
			}
		}));
	}, [data, isLoading, defaultPlatforms]);

	return (
		<div className="selected-item-table-div">
			{(appliedPlatformNames?.[0] == "all" || appliedPlatformNames?.length > 0) &&
				defaultPlatforms?.length > 0 &&
				tableData &&
				tableData.length > 1 && (
					<ReactTable
						classes="item-selected-item-table"
						loading={isLoading}
						error={error}
						data={isLoading || error ? duplicateValues : data}
						columns={newColumns}
						colsArray={columnsData}
						thStyles={{
							cols: ["location"],
							styles: {
								width: platformCount,
								textAlign: "end"
							}
						}}
						limit={limit}
						offset={offset}
						goToPage={handlePagination}
						setPageSize={handlePageSize}
						count={dataCount}
						fetchData={refreshBtn}
					/>
				)}
		</div>
	);
};

const mapStateToProps = (state) => ({
	periscopeSpecificItemAvailabilityItemData: state.periscopeSpecificItemAvailabilityItemData,
	periscopeSpecificItemAvailabilityItemState: state.periscopeSpecificItemAvailabilityItemState,
	configItems: state.configItems,
	periscopeCountryWiseSupportedPlatforms: state.periscopeCountryWiseSupportedPlatforms,
	login: state.login
});

export default connect(mapStateToProps)(SelectedItemTable);
